import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { Observable, of, ReplaySubject } from 'rxjs';
import { map, take, tap } from 'rxjs/operators';
import { BillingSettings, NotificationsSettings, PersonalInformation, SecuritySettings, TutorialProgress, User } from 'app/core/user/user.types';
import { Router } from '@angular/router';
import { UtilService } from 'app/shared/util.service';

@Injectable({
    providedIn: 'root'
})
export class UserService {
    private _user: ReplaySubject<User>;
    private _adminUser: ReplaySubject<User>;

    constructor(private _httpClient: HttpClient, private _router: Router, private _utilService: UtilService) {
    }

    // -----------------------------------------------------------------------------------------------------
    // @ Accessors
    // -----------------------------------------------------------------------------------------------------

    /**
     * Setter & getter for user
     *
     * @param value
     */
    set user(value: User) {
        if (!this._user) {
            this._user = new ReplaySubject<User>(1);
        }
        this._user.next(value);
    }

    get user$(): Observable<User> {
        if (!this._user) {
            this._user = new ReplaySubject<User>(1);
            this.get().pipe(take(1)).subscribe({
                next: (success) => success,
                error: (error) => {
                    console.error('User error:', error);
                    if (error.status === 401) {
                        this._router.navigate(['/sign-in']);
                    }
                    return of(true);
                }
            });
        }

        return this._user.asObservable();
    }

    set adminUser(value: User) {
        if (!this._adminUser) {
            this._adminUser = new ReplaySubject<User>(1);
        }
        this._adminUser.next(value);
    }

    get adminUser$(): Observable<User> {
        if (!this._adminUser) {
            return of(null);
        }

        return this._adminUser.asObservable();
    }

    // -----------------------------------------------------------------------------------------------------
    // @ Public methods
    // -----------------------------------------------------------------------------------------------------

    /**
     * Get the currently logged in user's data
     */
    get(): Observable<User> {
        return this._httpClient.get<User>('api/user').pipe(
            tap((user) => {
                if (!this._user) {
                    this._user = new ReplaySubject<User>(1);
                }
                this._user.next(user);
            }),
        );
    }

    getById(userId: string): Observable<User> {
        return this._httpClient.get<User>('api/user/by-uuid/' + userId).pipe(
            tap((user) => {
                if (!this._user) {
                    this._user = new ReplaySubject<User>(1);
                }
            }),
        );
    }

    setLocalTutorialProgress(progress: Partial<TutorialProgress>) {
        let user: User;
        this.user$.pipe(take(1)).subscribe((u) => user = u);
        if (!user.tutorialProgress) {
            return;
        }

        user.tutorialProgress = {...user.tutorialProgress, ...progress};
        this._user.next(user);
    }

    /**
     * Update the user
     *
     * @param user
     */
    update(user: User): Observable<any> {
        return this._httpClient.patch<User>('api/user', {userId: user.uuid, personalInformation: user.personalInformation, notes: user.notes}).pipe(
            map((response) => {
                this._user.next(response);
                return response;
            })
        );
    }

    updatePersonalInformation(personalInformation: PersonalInformation): Observable<User> {
        //Remove time from dob
        if (personalInformation.dob) {
            let dob = new Date(personalInformation.dob);
            personalInformation.dob = this._utilService.getDateString(dob);
        }

        return this._httpClient.patch<User>('api/user/personal-information', personalInformation).pipe(
            map((user) => {
                this.setLocalTutorialProgress({profileCompleted: true});
                this._user.next(user);
                return user;
            })
        );
    }

    updateBillingSettings(billing: BillingSettings, password: string): Observable<User> {
        return this._httpClient.patch<User>('api/user/billing', {billingSettings: billing, password: password}).pipe(
            map((user) => {
                this._user.next(user);
                return user;
            })
        );
    }

    updateNotificationsSettings(notifications: NotificationsSettings) {
        return this._httpClient.patch<User>('api/user/notification-settings', notifications).pipe(
            map((user) => {
                this._user.next(user);
                return user;
            })
        );
    }

    updateSecuritySettings(security: SecuritySettings) {
        return this._httpClient.patch<void>('api/user/password', security);
    }
}
